import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields, listChanges } from "@/lib/changed-fields";
import type { RowSelectionState, SortingState } from "@tanstack/react-table";
import { pollExportStatus } from "@/components/data-table/use-csv-export";
import type { RootStore } from "./root.store";
import type {
  TSkill,
  TSkillBulkImportResult,
  TSkillBulkImportRow,
  TUserSkillAssignment,
  TWorkspaceSkillsListQuery,
  TWorkspaceSkillsPage,
} from "@/types/skill";

function sortSkills(skills: TSkill[]): TSkill[] {
  return [...skills].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}

const EMPTY_SKILLS: readonly TSkill[] = Object.freeze([]);
const EMPTY_USERS: readonly TUserSkillAssignment[] = Object.freeze([]);

/** Server-side cap on a list row's `preview_user_ids` (`bounded_preview_map`). */
const PREVIEW_USER_ID_LIMIT = 3;

/** The first few assignee ids as the list endpoint would order them.
 *
 *  The server's preview orders assignments by `(created_at, id)`, so the cached
 *  row has to sort the same way or the closed cell shows different faces before
 *  and after a refresh. Sorting is required, not defensive: the PUT re-reads
 *  without an `order_by`, so its response arrives in whatever order Postgres
 *  returns. The `id` tiebreak carries the weight in practice — a replace-save
 *  writes the whole set in one `bulk_create`, so the rows share a `created_at`
 *  — and comparing canonical lowercase UUID strings matches Postgres's `uuid`
 *  ordering, since the hyphens sit at fixed positions and hex digits sort in
 *  nibble order. */
function previewUserIds(assignments: TUserSkillAssignment[]): string[] {
  return [...assignments]
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id))
    .slice(0, PREVIEW_USER_ID_LIMIT)
    .map((assignment) => assignment.user);
}

const SKILLS_DEFAULT_PER_PAGE = 50;

export type SkillStatusFilter = "active" | "archived";

interface PageState {
  results: TSkill[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  totalCount: number;
  totalPages: number;
}

const EMPTY_PAGE_STATE: PageState = {
  results: [],
  nextCursor: null,
  prevCursor: null,
  hasNext: false,
  hasPrev: false,
  totalCount: 0,
  totalPages: 0,
};

function envelopeToPageState(page: TWorkspaceSkillsPage): PageState {
  return {
    results: page.results,
    nextCursor: page.next_page_results ? page.next_cursor : null,
    prevCursor: page.prev_page_results ? page.prev_cursor : null,
    hasNext: page.next_page_results,
    hasPrev: page.prev_page_results,
    totalCount: page.total_count,
    totalPages: page.total_pages,
  };
}

export class WorkspaceSkillsStore {
  // Full-list cache (walked across pages) used by pickers — intake palette,
  // profile skills, team sidebar, availability. These never need paging.
  skillsBySlug = observable.map<string, TSkill[]>();
  // Current paginated page used by the workspace settings management list.
  pageBySlug = observable.map<string, PageState>();
  // Cursor the current page was loaded with — null means "first page".
  cursorBySlug = observable.map<string, string | null>();
  usersBySkillId = observable.map<string, TUserSkillAssignment[]>();
  skillsByMemberId = observable.map<string, TUserSkillAssignment[]>();

  // Filter state for the management list. Pushed in by the route on every
  // column-filter change; `loadPage` reads these to build the query.
  searchQuery = "";
  userIdsFilter = observable.set<string>();
  statusFilter = observable.set<SkillStatusFilter>();
  // Single-column sort for the management list (server-side via `order_by`).
  sorting: SortingState = [];

  isLoading = false;
  isLoadingMore = false;
  isMutating = false;
  activeSkillId: string | null = null;
  // Last list-load failure, surfaced to the DataTable `error` prop. Cleared
  // when a (re)load starts; set when loadPage/loadMore reject.
  loadError: Error | null = null;
  // Row-selection state for the management list (drives CSV-of-selection).
  rowSelection: RowSelectionState = {};

  private rootStore: RootStore;
  private inflightFullList = new Map<string, Promise<TSkill[]>>();
  private inflightPage = new Map<string, Promise<PageState>>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      { rootStore: false, inflightFullList: false, inflightPage: false } as never,
      { autoBind: true },
    );
  }

  // ---- Read getters -----------------------------------------------------

  get skills(): TSkill[] {
    const slug = this.rootStore.router.workspaceSlug;
    if (!slug) return EMPTY_SKILLS as TSkill[];
    return this.skillsBySlug.get(slug) ?? (EMPTY_SKILLS as TSkill[]);
  }

  get pagedSkills(): TSkill[] {
    const slug = this.rootStore.router.workspaceSlug;
    if (!slug) return EMPTY_SKILLS as TSkill[];
    return (this.pageBySlug.get(slug) ?? EMPTY_PAGE_STATE).results;
  }

  get pagination(): PageState {
    const slug = this.rootStore.router.workspaceSlug;
    if (!slug) return EMPTY_PAGE_STATE;
    return this.pageBySlug.get(slug) ?? EMPTY_PAGE_STATE;
  }

  get filteredSkills(): TSkill[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return this.skills;
    return this.skills.filter((skill) => skill.name.toLowerCase().includes(query));
  }

  getSkillsForSlug(slug: string): TSkill[] {
    return this.skillsBySlug.get(slug) ?? (EMPTY_SKILLS as TSkill[]);
  }

  getSkillUsers(skillId: string): TUserSkillAssignment[] {
    return this.usersBySkillId.get(skillId) ?? (EMPTY_USERS as TUserSkillAssignment[]);
  }

  /** Whether this skill's assignments were loaded (or mutated) this session —
   *  distinguishes "not fetched" from "has no assignees", so the Members cell
   *  can prefer the fresher store copy over the list row's bounded
   *  `preview_user_ids`. */
  hasSkillUsers(skillId: string): boolean {
    return this.usersBySkillId.has(skillId);
  }

  getMemberSkills(memberId: string): TUserSkillAssignment[] {
    return this.skillsByMemberId.get(memberId) ?? (EMPTY_USERS as TUserSkillAssignment[]);
  }

  /** Whether assignments for this member were loaded (or mutated) this
   *  session — distinguishes "not fetched" from "has no skills" so callers
   *  can prefer the fresher store copy over the row payload's skill_ids. */
  hasMemberSkills(memberId: string): boolean {
    return this.skillsByMemberId.has(memberId);
  }

  // ---- Filter setters ---------------------------------------------------

  setSearchQuery(query: string): void {
    this.searchQuery = query;
  }

  setUserIdsFilter(ids: Iterable<string>): void {
    this.userIdsFilter.replace([...ids]);
  }

  setStatusFilter(values: Iterable<SkillStatusFilter>): void {
    this.statusFilter.replace([...values]);
  }

  /** Set the single-column sort. The route reloads the first page after. */
  setSorting(next: SortingState): void {
    this.sorting = next;
  }

  setRowSelection(next: RowSelectionState): void {
    this.rowSelection = next;
  }

  /** Export the skills list as CSV, scoped to the current filters/sort and (when
   *  `ids` is non-empty) restricted to the selected rows. Returns the raw
   *  Response so the caller can stream the file (200) or poll the token (202). */
  exportSkillsCsv(slug: string, ids?: string[], columns?: string[]): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids && ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.rootStore.workspaceSkillService.exportSkills(slug, body, this.buildListQuery(null));
  }

  /** Poll an async export by token until its presigned URL is ready (or null). */
  pollExport(slug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) =>
      this.rootStore.workspaceSkillService.getExportStatus(slug, tok),
    );
  }

  clearListFilters(): void {
    this.searchQuery = "";
    this.userIdsFilter.clear();
    this.statusFilter.clear();
    this.sorting = [];
  }

  // ---- Page-driven (management list) fetch ------------------------------

  private buildListQuery(cursor: string | null): TWorkspaceSkillsListQuery {
    const query: TWorkspaceSkillsListQuery = {
      per_page: SKILLS_DEFAULT_PER_PAGE,
    };
    const trimmed = this.searchQuery.trim();
    if (trimmed) query.q = trimmed;
    if (this.userIdsFilter.size > 0) query.user_ids = [...this.userIdsFilter];
    // Status filter is a multi-select; only forward `archived` to the BE
    // when exactly one is selected — selecting both is equivalent to "no
    // filter", and the BE has no tri-state filter on a single param.
    if (this.statusFilter.size === 1) {
      const [only] = [...this.statusFilter];
      query.archived = only === "archived";
    }
    const sort = this.sorting[0];
    if (sort) query.order_by = `${sort.desc ? "-" : ""}${sort.id}`;
    if (cursor) query.cursor = cursor;
    return query;
  }

  async loadPage(slug: string, cursor: string | null = null): Promise<void> {
    const query = this.buildListQuery(cursor);
    const key = `${slug}:${JSON.stringify(query)}`;
    const existing = this.inflightPage.get(key);
    if (existing) {
      await existing;
      return;
    }
    const promise = (async () => {
      runInAction(() => {
        this.isLoading = true;
        this.loadError = null;
      });
      try {
        const page = await this.rootStore.workspaceSkillService.list(slug, query);
        const state = envelopeToPageState(page);
        runInAction(() => {
          this.pageBySlug.set(slug, state);
          this.cursorBySlug.set(slug, cursor);
        });
        return state;
      } catch (err) {
        runInAction(() => {
          this.loadError = err instanceof Error ? err : new Error(String(err));
        });
        throw err;
      } finally {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    })().finally(() => {
      this.inflightPage.delete(key);
    });
    this.inflightPage.set(key, promise);
    await promise;
  }

  /** Reload the current page with the current filters. Mutations call this
   *  to keep server-side filter/sort membership accurate. */
  async refreshCurrentPage(slug: string): Promise<void> {
    if (!this.pageBySlug.has(slug)) return;
    const cursor = this.cursorBySlug.get(slug) ?? null;
    await this.loadPage(slug, cursor);
  }

  /** Append the next page to the current results (DataTable infinite scroll).
   *  No-op when there's no next page or a fetch is already in flight. */
  async loadMore(slug: string): Promise<void> {
    const current = this.pageBySlug.get(slug);
    // No page yet (initial load failed) — the DataTable error-empty "Try again"
    // routes the retry here, so re-run the first page rather than no-opping.
    if (!current) {
      await this.loadPage(slug, null);
      return;
    }
    if (!current.hasNext || !current.nextCursor) return;
    if (this.isLoadingMore) return;
    const query = this.buildListQuery(current.nextCursor);
    runInAction(() => {
      this.isLoadingMore = true;
      this.loadError = null;
    });
    try {
      const page = await this.rootStore.workspaceSkillService.list(slug, query);
      const next = envelopeToPageState(page);
      runInAction(() => {
        const existing = this.pageBySlug.get(slug);
        const seen = new Set((existing?.results ?? []).map((s) => s.id));
        const merged = [
          ...(existing?.results ?? []),
          ...next.results.filter((s) => !seen.has(s.id)),
        ];
        this.pageBySlug.set(slug, { ...next, results: merged });
        this.cursorBySlug.set(slug, current.nextCursor);
      });
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }

  // ---- Full-list fetch (pickers) ----------------------------------------

  /** Walks every page and caches the flat list. Used by pickers (intake,
   *  team sidebar, profile, availability). Kept for source-compat with the
   *  legacy bare-array contract. */
  async fetchSkills(slug: string, options?: TWorkspaceSkillsListQuery): Promise<TSkill[]> {
    this.isLoading = true;
    try {
      const list = await this.rootStore.workspaceSkillService.fetchAll(slug, options ?? {});
      runInAction(() => {
        this.skillsBySlug.set(slug, sortSkills(list));
      });
      return list;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /** Concurrent-safe ensure for the full-list path. */
  async ensureSkills(slug: string, options?: TWorkspaceSkillsListQuery): Promise<TSkill[]> {
    const cached = this.skillsBySlug.get(slug);
    if (cached) return cached;
    const inflight = this.inflightFullList.get(slug);
    if (inflight) return inflight;
    const p = this.fetchSkills(slug, options).finally(() => {
      this.inflightFullList.delete(slug);
    });
    this.inflightFullList.set(slug, p);
    return p;
  }

  // ---- Mutations --------------------------------------------------------

  async createSkill(slug: string, name: string, source: AnalyticsSource = "page"): Promise<TSkill> {
    this.isMutating = true;
    try {
      const created = await this.rootStore.workspaceSkillService.create(slug, { name });
      runInAction(() => {
        const next = [...(this.skillsBySlug.get(slug) ?? []), created];
        this.skillsBySlug.set(slug, sortSkills(next));
      });
      // Before the refresh, so a failed reload doesn't drop it (NG-2063).
      track("skill_created", { skill_id: created.id, source });
      if (this.pageBySlug.has(slug)) await this.refreshCurrentPage(slug);
      return created;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  /** Bulk-import skills from parsed CSV rows (NG-661). Returns the per-row
   *  report; refreshes the current page so imported skills appear immediately. */
  async bulkImport(slug: string, rows: TSkillBulkImportRow[]): Promise<TSkillBulkImportResult> {
    this.isMutating = true;
    try {
      const report = await this.rootStore.workspaceSkillService.bulkImport(slug, rows);
      track("skills_bulk_imported", {
        created: report.created_count,
        reused: report.reused_count,
        errors: report.error_count,
        source: "import",
      });
      if (this.pageBySlug.has(slug)) await this.refreshCurrentPage(slug);
      return report;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async updateSkill(
    slug: string,
    skillId: string,
    data: { name?: string; sort_order?: number },
    source: AnalyticsSource = "page",
  ): Promise<TSkill> {
    this.activeSkillId = skillId;
    // Read before the write: `fields` is what this save changed.
    const fields = changedFields(
      data,
      (this.skillsBySlug.get(slug) ?? []).find((skill) => skill.id === skillId),
    );
    try {
      const updated = await this.rootStore.workspaceSkillService.update(slug, skillId, data);
      this.replaceSkill(slug, updated);
      track("skill_updated", { skill_id: skillId, fields, source });
      return updated;
    } finally {
      runInAction(() => {
        this.activeSkillId = null;
      });
    }
  }

  async archiveSkill(
    slug: string,
    skillId: string,
    source: AnalyticsSource = "page",
  ): Promise<TSkill> {
    this.activeSkillId = skillId;
    try {
      const updated = await this.rootStore.workspaceSkillService.archive(slug, skillId);
      this.replaceSkill(slug, updated);
      track("skill_archived", { skill_id: skillId, source });
      return updated;
    } finally {
      runInAction(() => {
        this.activeSkillId = null;
      });
    }
  }

  async unarchiveSkill(
    slug: string,
    skillId: string,
    source: AnalyticsSource = "page",
  ): Promise<TSkill> {
    this.activeSkillId = skillId;
    try {
      const updated = await this.rootStore.workspaceSkillService.unarchive(slug, skillId);
      this.replaceSkill(slug, updated);
      track("skill_restored", { skill_id: skillId, source });
      return updated;
    } finally {
      runInAction(() => {
        this.activeSkillId = null;
      });
    }
  }

  async loadSkillUsers(slug: string, skillId: string): Promise<TUserSkillAssignment[]> {
    const list = await this.rootStore.workspaceSkillService.listSkillUsers(slug, skillId);
    runInAction(() => {
      this.usersBySkillId.set(skillId, list);
    });
    return list;
  }

  async setSkillUsers(
    slug: string,
    skillId: string,
    userIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<TUserSkillAssignment[]> {
    this.activeSkillId = skillId;
    // The call sends the whole list: count what it adds and removes against the
    // holders already loaded. A list never loaded counts only the adds.
    const prior = (this.usersBySkillId.get(skillId) ?? []).map((a) => a.user);
    try {
      const list = await this.rootStore.workspaceSkillService.setSkillUsers(slug, skillId, userIds);
      const { added, removed } = listChanges(prior, userIds);
      if (added > 0) track("skill_assigned", { skill_id: skillId, count: added, source });
      if (removed > 0) track("skill_unassigned", { skill_id: skillId, count: removed, source });
      runInAction(() => {
        this.usersBySkillId.set(skillId, list);
        // Patch `preview_user_ids` with the count: the cell prefers the
        // assignment list above while it's cached, but the cached ROW is what
        // survives that cache being dropped (e.g. `setMemberSkills` invalidating
        // it), and a row carrying a new count next to a stale preview is a row
        // that contradicts itself.
        const patch = { user_count: list.length, preview_user_ids: previewUserIds(list) };
        const current = this.skillsBySlug.get(slug) ?? [];
        this.skillsBySlug.set(
          slug,
          sortSkills(
            current.map((skill) => (skill.id === skillId ? { ...skill, ...patch } : skill)),
          ),
        );
        // Also patch the paged page so the management list reflects the
        // new count without a round-trip.
        const page = this.pageBySlug.get(slug);
        if (page) {
          this.pageBySlug.set(slug, {
            ...page,
            results: page.results.map((skill) =>
              skill.id === skillId ? { ...skill, ...patch } : skill,
            ),
          });
        }
      });
      return list;
    } finally {
      runInAction(() => {
        this.activeSkillId = null;
      });
    }
  }

  async deleteSkill(
    slug: string,
    skillId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeSkillId = skillId;
    try {
      await this.rootStore.workspaceSkillService.remove(slug, skillId);
      track("skill_deleted", { skill_id: skillId, source });
      runInAction(() => {
        const current = this.skillsBySlug.get(slug) ?? [];
        this.skillsBySlug.set(
          slug,
          current.filter((skill) => skill.id !== skillId),
        );
      });
      if (this.pageBySlug.has(slug)) await this.refreshCurrentPage(slug);
    } finally {
      runInAction(() => {
        this.activeSkillId = null;
      });
    }
  }

  async loadMemberSkills(slug: string, memberId: string): Promise<TUserSkillAssignment[]> {
    const list = await this.rootStore.workspaceSkillService.listMemberSkills(slug, memberId);
    runInAction(() => {
      this.skillsByMemberId.set(memberId, list);
    });
    return list;
  }

  async setMemberSkills(
    slug: string,
    memberId: string,
    skillIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<TUserSkillAssignment[]> {
    const oldAssignments = this.skillsByMemberId.get(memberId) ?? [];
    const oldSkillIds = new Set(oldAssignments.map((a) => a.skill));
    const list = await this.rootStore.workspaceSkillService.setMemberSkills(
      slug,
      memberId,
      skillIds,
    );
    // A person's skills, set from their side: the same events, keyed by member.
    const { added, removed } = listChanges(oldSkillIds, skillIds);
    if (added > 0) track("skill_assigned", { member_id: memberId, count: added, source });
    if (removed > 0) track("skill_unassigned", { member_id: memberId, count: removed, source });
    runInAction(() => {
      this.skillsByMemberId.set(memberId, list);
      const newSkillIds = new Set(list.map((a) => a.skill));
      for (const sid of oldSkillIds) {
        if (!newSkillIds.has(sid)) this.usersBySkillId.delete(sid);
      }
      for (const sid of newSkillIds) {
        if (!oldSkillIds.has(sid)) this.usersBySkillId.delete(sid);
      }
    });
    return list;
  }

  private replaceSkill(slug: string, updated: TSkill): void {
    runInAction(() => {
      const current = this.skillsBySlug.get(slug) ?? [];
      this.skillsBySlug.set(
        slug,
        sortSkills(current.map((skill) => (skill.id === updated.id ? updated : skill))),
      );
      const page = this.pageBySlug.get(slug);
      if (page) {
        this.pageBySlug.set(slug, {
          ...page,
          results: page.results.map((skill) => (skill.id === updated.id ? updated : skill)),
        });
      }
    });
  }
}
